/**
 * ScoreGauge — the 0–100 cabinet score as a ring.
 *
 * The ring's colour is derived from which band the score falls into, so a
 * red ring is a consequence of the published bands rather than a decision
 * made in the layout. That matters: the whole point of showing bands next to
 * the number is to prove the score isn't theatre.
 *
 * No chart library in the design system (D4) — this is hand-rolled SVG.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {SCORE_ZONE_TONE, paletteTone, type PaletteTone} from '../content';
import type {ScoreZone} from '../types';

export interface ScoreGaugeProps {
  value: number;
  max: number;
  zones: ScoreZone[];
  /** Outer diameter in px. */
  size?: number;
}

const SIZE = 180;
const STROKE = 13;

const Wrap = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: number}>(({$size}) => ({
  position: 'relative',
  width: $size,
  height: $size,
  flex: 'none',
}));

const Center = styled(Stack)({
  position: 'absolute',
  inset: 0,
  alignItems: 'center',
  justifyContent: 'center',
});

const Value = styled(Box)({
  ...text('heading.xl'),
  fontSize: '46px',
  lineHeight: '46px',
  color: surfaceVar('onSurface'),
});

const Caption = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
});

/**
 * В какую полосу попал балл. Границы включительные с обеих сторон — так
 * их задаёт API, и на стыке (45 / 46) полосы не спорят за одно значение.
 */
export function toneForScore(value: number, zones: ScoreZone[]): PaletteTone {
  const zone = zones.find(z => value >= z.from && value <= z.to);
  return zone ? paletteTone(SCORE_ZONE_TONE[zone.key]) : 'bad';
}

export function ScoreGauge({value, max, zones, size = SIZE}: ScoreGaugeProps) {
  const radius = (size - STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(1, value / max));
  const tone = toneForScore(value, zones);

  return (
    <Wrap $size={size}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{transform: 'rotate(-90deg)'}}
        aria-hidden
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='none'
          strokeWidth={STROKE}
          stroke={surfaceVar('surface3')}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='none'
          strokeWidth={STROKE}
          strokeLinecap='round'
          stroke={`var(--ads-palette-${tone}-main)`}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress)}
        />
      </svg>
      <Center gap='tight' alignX='center'>
        <Value role='img' aria-label={`${value} из ${max} баллов`}>
          {value}
        </Value>
        <Caption>из {max} баллов</Caption>
      </Center>
    </Wrap>
  );
}
